import { Button } from "@itk/mui-components";
import { Box, Stack, TextField, Typography, useTheme } from "@mui/material";
import { DekoxIcon } from "src/components";
import { configuratorPageK, useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import { addEditConfigurationDialogActions } from "src/pages/configurator/components/add-edit-configuration-dialog/add-edit-configuration-dialog-store";
import { configureProductActions } from "../../configure-product-store";
import { configuratorActions } from "src/pages/configurator/configurator-store";
import { applicationActions } from "src/components/application-store";
import { ProductsListSectionLoading } from "./products-list-section-loading";
import { useParams, useSearchParams } from "react-router-dom";
import { TConfigureProductSearchParams } from "../../configure-product-types";
import { useEffectAfterMount } from "src/utils/hooks/useEffectAfterMount";
import { LabelsMultiSelectChip } from "src/pages/configurator/components";
import { Model_ConfigurationGet_SystemProductConfigurationItem, Shared_SystemSettings } from "src/backend/openapi/v1";
import { getSystemProductConfigurationItemsHierarchy, collectExpandableItemIds } from "src/pages/configurator/configurator-utils";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { ProductTreeItems } from "./product-tree-items";

const mapStateToProps = (state: RootState) => ({
  addEditConfigurationDialog: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.addEditConfigurationDialog,
  configurationState: state.pageReducer.configureProductReducer.configurationState,
  selectedProjectLineItemId: state.pageReducer.configureProductReducer.selectedProjectLineItemId,
  copyConfigurationItemsLimit: parseInt(state.generalReducer.applicationReducer.systemSettings[Shared_SystemSettings.CopyConfigurationItemsLimit] ?? "", 10)
});

const ownActions = {
  setAddEditConfigurationDialog: addEditConfigurationDialogActions.setAddEditConfigurationDialog,
  createOrUpdateConfigurationRequest: addEditConfigurationDialogActions.createOrUpdateConfigurationRequest,
  setFormData: addEditConfigurationDialogActions.setFormData,
  setSelectedProjectLineItemId: configureProductActions.setSelectedProjectLineItemId,
  updateConfigurationDescriptionRequest: configureProductActions.updateConfigurationDescriptionRequest,
  getConfigurationRequest: configureProductActions.getConfigurationRequest,
  deleteProjectLineItemsRequest: configuratorActions.deleteProjectLineItemsRequest,
  copyProjectLineItemsRequest: configuratorActions.copyProjectLineItemsRequest,
  getLabelsRequest: configuratorActions.getLabelsRequest,
  setWarningAction: applicationActions.setWarningAction
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const ProductListSectionBase = (props: Props) => {
  const {
    setAddEditConfigurationDialog,
    configurationState,
    setSelectedProjectLineItemId,
    setFormData,
    selectedProjectLineItemId,
    updateConfigurationDescriptionRequest,
    getConfigurationRequest,
    deleteProjectLineItemsRequest,
    copyProjectLineItemsRequest,
    getLabelsRequest,
    setWarningAction,
    copyConfigurationItemsLimit
  } = props;

  const theme = useTheme();

  const hasValidCopyLimit = Number.isInteger(copyConfigurationItemsLimit) && copyConfigurationItemsLimit >= 1;

  const [searchParams] = useSearchParams();
  const { projectId = "", configurationId = "" } = useParams<{ projectId: string; configurationId: string }>();

  const genericTranslationsT = useGenericTranslationsT();
  const configuratorPageT = useConfiguratorPageT();

  const [configuratorName, setConfiguratorName] = useState("");

  const isInitializingName = useRef(false);

  // Build the nested tree only once data is loaded; falls back to [] otherwise so hook order stays stable.
  const systemProductConfigurationItemHierarchy = useMemo(
    () => (configurationState.loading === "has-loaded" ? getSystemProductConfigurationItemsHierarchy(configurationState.data.systemProductConfigurationItems) : []),
    [configurationState]
  );

  // Every node with children, so the tree renders fully expanded on first load
  const defaultExpandedItemIds = useMemo(() => collectExpandableItemIds(systemProductConfigurationItemHierarchy), [systemProductConfigurationItemHierarchy]);

  // Initialize configurator name
  useEffect(() => {
    if (configurationState.loading === "has-loaded") {
      if (!configuratorName) {
        isInitializingName.current = true;
        setConfiguratorName(configurationState.data.systemProductConfigurationName || "");
      }
    }
  }, [configurationState]);

  // debounce and save systemProductConfigurationName
  useEffectAfterMount(() => {
    if (isInitializingName.current) {
      isInitializingName.current = false;
      return;
    }
    const timer = setTimeout(() => {
      saveconfigurationName();
    }, 1000);

    return () => clearTimeout(timer);
  }, [configuratorName]);

  // Initialize selectedProjectLineItemId
  useEffect(() => {
    if (configurationState.loading === "has-loaded") {
      // systemProductConfigurationItems replaces projectLineItems; items use systemProductConfigurationItemId
      const index = configurationState.data.systemProductConfigurationItems.findIndex(
        projectLineItem =>
          projectLineItem.systemProductConfigurationItemId === selectedProjectLineItemId ||
          (!selectedProjectLineItemId && projectLineItem.systemProductConfigurationItemId === searchParams.get("projectLineItemId" as keyof TConfigureProductSearchParams))
      );
      const resolvedProjectLineItemId = configurationState.data.systemProductConfigurationItems?.[index >= 0 ? index : 0]?.systemProductConfigurationItemId;

      // configurationState gets a new object reference on every attribute save (to patch this list's
      // badges), re-running this effect without an actual selection change — only dispatch when the
      // resolved id is genuinely different, otherwise this causes an unnecessary re-render on every save.
      if (resolvedProjectLineItemId && resolvedProjectLineItemId !== selectedProjectLineItemId) {
        setSelectedProjectLineItemId(resolvedProjectLineItemId);
      }
    }
  }, [configurationState, selectedProjectLineItemId]);

  const saveconfigurationName = () => {
    if (configurationState.loading !== "has-loaded") return;
    // projectId/systemPackageid are intentionally omitted so the backend only updates the description
    updateConfigurationDescriptionRequest({
      systemProductConfigurationId: configurationState.data.systemProductConfigurationId,
      description: configuratorName
    });
  };

  const handleAddProduct = (projectLineItem: Model_ConfigurationGet_SystemProductConfigurationItem) => {
    if (!projectLineItem) return;

    setFormData({
      productsFilter: {
        // systemProductConfigurationItemId replaces projectLineItemId on the single-config model
        parentProjectLineItemId: projectLineItem.systemProductConfigurationItemId,
        productTypes: [projectLineItem.nextProductTypeId || ""],
        systemPackageId: projectLineItem.systemPackageId
      },
      systemProductConfigurationId: configurationState.loading === "has-loaded" ? configurationState.data.systemProductConfigurationId : undefined
    });
    setAddEditConfigurationDialog({ step: "SELECT PRODUCT" });
  };

  // Same confirm-then-delete flow as the list-view's per-item delete (product-configuration.tsx)
  const handleDeleteProduct = (projectLineItem: Model_ConfigurationGet_SystemProductConfigurationItem) => {
    setWarningAction({
      confirm: () => {
        if (!projectLineItem.systemProductConfigurationItemId) return;
        deleteProjectLineItemsRequest({ systemProductConfigurationItemIds: [projectLineItem.systemProductConfigurationItemId] }, isSuccess => {
          if (isSuccess) {
            getConfigurationRequest(configurationId);
          }
        });
        setWarningAction(undefined);
      },
      dialogProps: {
        message: configuratorPageT.areYouSureYouWantToDeleteThisProductConfiguration,
        confirmButtonTranslation: {
          t: genericTranslationsT,
          k: genericTranslationsK,
          field: "delete"
        }
      },
      variant: "confirmCancel"
    });
  };

  // Same shape as handleDeleteProduct, but no confirm-are-you-sure step — copy has only its own blank/values+count prompt
  const handleCopyProduct = (projectLineItem: Model_ConfigurationGet_SystemProductConfigurationItem, blankValues: boolean, count: number) => {
    if (!projectLineItem.systemProductConfigurationItemId) return;

    copyProjectLineItemsRequest(blankValues, { systemProductConfigurationItemIds: [projectLineItem.systemProductConfigurationItemId], count }, isSuccess => {
      if (isSuccess) {
        getConfigurationRequest(configurationId);
        getLabelsRequest(projectId);
      }
    });
  };

  if (configurationState.loading === "is-loading") return <ProductsListSectionLoading />;

  if (configurationState.loading === "has-loaded")
    return (
      <Stack gap={4}>
        {/* Title */}
        <Typography variant="h2" fontWeight="500" color={theme.palette.primary.contrastText}>
          {configuratorPageT.configuration}
        </Typography>

        {/* Configurator name and label */}
        <Stack gap={1}>
          <TextField
            autoFocus
            value={configuratorName}
            onChange={e => {
              setConfiguratorName(e.target.value);
            }}
            size="small"
            label={genericTranslationsT.name}
            sx={{
              fontSize: theme.typography.caption.fontSize,
              "& .MuiOutlinedInput-root": {
                bgcolor: theme.palette.customColors.whiteFaint,
                color: theme.palette.primary.contrastText,
                "& fieldset": { borderColor: theme.palette.customColors.whiteBorder },
                "&:hover fieldset": { borderColor: theme.palette.customColors.whiteStrong },
                "&.Mui-focused fieldset": { borderColor: theme.palette.primary.contrastText }
              },
              "& .MuiInputLabel-root": {
                color: theme.palette.customColors.whiteStrong,
                "&.Mui-focused": { color: theme.palette.primary.contrastText }
              }
            }}
            onKeyDown={e => e.key === "Enter" && saveconfigurationName()}
          />
          <LabelsMultiSelectChip
            key={configurationState.data.systemProductConfigurationId}
            projectId={projectId}
            entityIds={[configurationState.data.systemProductConfigurationId || ""]}
            applyOnBlur
            darkMode
            floatingLabelBg={theme.palette.primary.main}
          />
        </Stack>

        {/* Products */}
        <Stack gap={2}>
          <Typography variant="h6" fontWeight="500" color={theme.palette.primary.contrastText}>
            {configuratorPageT.products}
          </Typography>
          {systemProductConfigurationItemHierarchy.length === 0 && (
            <Box>
              <Button
                t={configuratorPageT}
                k={configuratorPageK}
                field="addProduct"
                variant="contained"
                color="inherit"
                startIcon={<DekoxIcon icon="add" />}
                onClick={() => {
                  setFormData({
                    systemProductConfigurationId: configurationState.data.systemProductConfigurationId
                  });
                  setAddEditConfigurationDialog({ step: "SELECT SYSTEM" });
                }}
              />
            </Box>
          )}
          {systemProductConfigurationItemHierarchy.length > 0 && (
            <SimpleTreeView
              expansionTrigger="iconContainer"
              defaultExpandedItems={defaultExpandedItemIds}
              disableSelection
              sx={{
                "& .MuiTreeItem-groupTransition": {
                  position: "relative",
                  pl: 2,
                  "&::before": {
                    content: "''",
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: `calc(${theme.spacing(1)} + var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth) + 8px)`,
                    borderLeft: `1px solid ${theme.palette.customColors.whiteChip}`,
                    pointerEvents: "none"
                  }
                },
                // The chevron now renders inside ProductLineItemRow itself (see the arrow-up/arrow-down
                // icon there) — the native icon container is redundant, hide it everywhere
                "& .MuiTreeItem-iconContainer": {
                  display: "none"
                },
                "& .MuiTreeItem-content:hover": {
                  backgroundColor: "transparent"
                },
                "& .MuiTreeItem-content[data-focused]": {
                  backgroundColor: "transparent"
                },
                "& .MuiTreeItem-label": {
                  overflow: "visible"
                }
              }}
            >
              <ProductTreeItems
                nodes={systemProductConfigurationItemHierarchy}
                selectedProjectLineItemId={selectedProjectLineItemId}
                onSelect={setSelectedProjectLineItemId}
                onAddChild={handleAddProduct}
                onDeleteItem={handleDeleteProduct}
                onCopy={handleCopyProduct}
                maxCount={hasValidCopyLimit ? copyConfigurationItemsLimit : undefined}
                theme={theme}
              />
            </SimpleTreeView>
          )}
        </Stack>
      </Stack>
    );

  return <></>;
};

export const ProductsListSection = connect(mapStateToProps, ownActions)(ProductListSectionBase);
